import React, { Fragment } from "react";
import authApi from "../../../utils/api/authApi";
import { Stack, Button, Paper, TextField } from "@mui/material";
import PageContentWrapper from "../../../components/wrappers/PageContentWrapper";
import * as Validation from "../utils/AuthZodValidation";
import { useForm } from "react-hook-form";
import { CHANGE_PASSWORD } from "../../../enums/ApiPaths";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import { useTranslation } from "react-i18next";
import CenterScreen from "../../../components/wrappers/CenterScreen";
import * as Functions from "../../../utils/Functions";
import usePrimaryColor from "../../../hooks/usePrimaryColor";
import { SnackbarContext } from "../../../contexts/Snackbar";
import useUser from "../../../hooks/useUser";
import AuthFooter from "../login/component/AuthFooter";
// This page is accessed from the user profile!
export default function UserChangePasswordPage({ id }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile } = useUser();
  const color = usePrimaryColor();
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const { register, handleSubmit, formState, setError } = useForm({
    defaultValues: Validation.defaultUserChangePasswordValues,
    resolver: zodResolver(Validation.useUserChangePasswordResolver()),
  });

  const { errors } = formState;

  const changePasswordSubmit = async (e) => {
    const header = await Functions.getBearerToken();
    const data = {
      email: profile.email,
      oldPassword: e.oldPassword,
      password: e.password,
    };
    try {
      let res = await authApi.post(CHANGE_PASSWORD, data, {
        headers: header,
      });

      if (res.status == HttpStatusCode.Ok) {
        Functions.setPasswordEmpty(false);
        handleSnackbar("PasswordChanged", "success");
        navigate(-1);
      }
    } catch (error) {
      if (error?.response?.data === "Invalid password") {
        setError("oldPassword", {
          type: "custom",
          message: t("WrongPassword"),
        });
      } else {
        handleSnackbar(error?.response?.data, "error");
      }
    }
  };

  return (
    <Fragment>
      <PageContentWrapper>
        <CenterScreen>
          <Paper sx={{ p: 2, width: { xs: "90%", md: "35%" } }}>
            <form onSubmit={handleSubmit(changePasswordSubmit)}>
              <Stack
                spacing={2}
                justifyContent={"center"}
                alignItems={"center"}
              >
                {Functions.isPasswordEmpty() !== "true" ? (
                  <TextField
                    sx={{ width: "100%", m: 2 }}
                    inputProps={{
                      maxLength: 64,
                      "data-testid": "auth.change-password.old-password-input",
                    }}
                    label={t("OldPassword")}
                    type="password"
                    error={!!errors.oldPassword}
                    helperText={errors.oldPassword?.message}
                    {...register("oldPassword")}
                  />
                ) : null}

                <TextField
                  sx={{ width: "100%", m: 2 }}
                  inputProps={{
                    maxLength: 64,
                    "data-testid": "auth.change-password.password-input",
                  }}
                  label={t("Password")}
                  type="password"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  {...register("password")}
                />
                <TextField
                  sx={{ width: "100%", m: 2 }}
                  inputProps={{
                    maxLength: 64,
                    "data-testid":
                      "auth.change-password.confirm-password-input",
                  }}
                  label={t("ConfirmPassword")}
                  type="password"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  {...register("confirmPassword")}
                />
                <Button
                  sx={{ width: "100%" }}
                  type="submit"
                  variant="contained"
                  data-testid="auth.change-password.submit-button"
                >
                  {t("ChangePassword")}
                </Button>
                <Button
                  sx={{ width: "100%" }}
                  onClick={() => navigate(-1)}
                  variant="outlined"
                  data-testid="auth.change-password.back-button"
                >
                  {t("Back")}
                </Button>
              </Stack>
            </form>
          </Paper>
        </CenterScreen>
      </PageContentWrapper>
      <AuthFooter />
    </Fragment>
  );
}
